import React, { useEffect, useState } from 'react';
import { AlertTriangle, Check, Edit, FileText, Plus, Trash2, X } from 'lucide-react';
import apiService from '../../services/api';
import TribunalPicker from './TribunalPicker';
import { JUSTICE_SPHERES } from '../../utils/justiceSphere';

const KIND_OPTIONS = [
  { value: 'INCLUDE', label: 'Aceitar', hint: 'A publicação precisa conter este termo na classe' },
  { value: 'EXCLUDE', label: 'Excluir', hint: 'Publicações com este termo na classe não serão ingeridas' },
];

const JUSTICE_COLORS = {
  comum: { color: 'bg-blue-100 text-blue-700', box: 'bg-blue-50 border-blue-200' },
  trabalho: { color: 'bg-green-100 text-green-700', box: 'bg-green-50 border-green-200' },
  federal: { color: 'bg-purple-100 text-purple-700', box: 'bg-purple-50 border-purple-200' },
};

const JUSTICE_OPTIONS = JUSTICE_SPHERES.map((sphere) => ({
  key: sphere.id,
  label: sphere.shortLabel,
  ...JUSTICE_COLORS[sphere.id],
}));

const EMPTY_JUSTICE = { comum: false, trabalho: false, federal: false };

function kindMeta(kind) {
  if (kind === 'EXCLUDE') {
    return {
      label: 'Excluir',
      badge: 'bg-red-100 text-red-700',
      column: 'bg-red-50 border-red-200',
      header: 'bg-red-100 text-red-800',
    };
  }

  return {
    label: 'Aceitar',
    badge: 'bg-emerald-100 text-emerald-700',
    column: 'bg-emerald-50 border-emerald-200',
    header: 'bg-emerald-100 text-emerald-800',
  };
}

function toJusticeSelection(types = []) {
  return {
    comum: types.includes('comum'),
    trabalho: types.includes('trabalho'),
    federal: types.includes('federal'),
  };
}

function selectedJusticeTypes(selection) {
  return Object.keys(selection).filter((key) => selection[key]);
}

function JusticeTypePicker({ value, onChange, idPrefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      {JUSTICE_OPTIONS.map((option) => (
        <label
          key={option.key}
          htmlFor={`${idPrefix}-${option.key}`}
          className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer ${option.box}`}
        >
          <input
            id={`${idPrefix}-${option.key}`}
            type="checkbox"
            checked={Boolean(value[option.key])}
            onChange={(event) =>
              onChange({
                ...value,
                [option.key]: event.target.checked,
              })
            }
            className="w-3.5 h-3.5 rounded border-gray-300"
          />
          <span className="text-xs font-medium text-gray-800">{option.label}</span>
        </label>
      ))}
    </div>
  );
}

export default function ClassesProcessuaisTab({ readOnly = false }) {
  const [classes, setClasses] = useState([]);
  const [tribunals, setTribunals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState('INCLUDE');
  const [justiceTypes, setJusticeTypes] = useState({
    comum: true,
    trabalho: true,
    federal: true,
  });
  const [tribunalSiglas, setTribunalSiglas] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    kind: 'INCLUDE',
    justiceTypes: { ...EMPTY_JUSTICE },
    tribunalSiglas: [],
  });

  async function fetchClasses({ silent = false } = {}) {
    try {
      if (!silent) setLoading(true);
      const [data, tribunalsData] = await Promise.all([
        apiService.get('/settings/process-classes'),
        apiService.get('/settings/tribunals'),
      ]);
      setClasses(data || []);
      setTribunals(tribunalsData || []);
    } catch (err) {
      setSuccess(null);
      setError(err.response?.data?.error || 'Erro ao carregar classes processuais');
      if (!silent) setClasses([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    fetchClasses();
  }, []);

  async function handleCreate(event) {
    event.preventDefault();
    if (!name.trim()) {
      setSuccess(null);
      setError('Informe o nome da classe processual');
      return;
    }

    const selectedTypes = selectedJusticeTypes(justiceTypes);
    if (selectedTypes.length === 0) {
      setSuccess(null);
      setError('Selecione ao menos uma esfera de justiça');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await apiService.post('/settings/process-classes', {
        name: name.trim(),
        kind,
        justiceTypes: selectedTypes,
        tribunalSiglas,
      });
      setName('');
      setKind('INCLUDE');
      setJusticeTypes({ comum: true, trabalho: true, federal: true });
      setTribunalSiglas([]);
      setShowForm(false);
      setSuccess('Classe processual cadastrada');
      await fetchClasses({ silent: true });
    } catch (err) {
      setSuccess(null);
      setError(err.response?.data?.error || 'Erro ao cadastrar classe processual');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(id) {
    if (!editForm.name.trim()) {
      setSuccess(null);
      setError('Informe o nome da classe processual');
      return;
    }

    const selectedTypes = selectedJusticeTypes(editForm.justiceTypes);
    if (selectedTypes.length === 0) {
      setSuccess(null);
      setError('Selecione ao menos uma esfera de justiça');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await apiService.put(`/settings/process-classes/${id}`, {
        name: editForm.name.trim(),
        kind: editForm.kind,
        justiceTypes: selectedTypes,
        tribunalSiglas: editForm.tribunalSiglas || [],
      });
      setEditingId(null);
      setSuccess('Classe processual atualizada');
      await fetchClasses({ silent: true });
    } catch (err) {
      setSuccess(null);
      setError(err.response?.data?.error || 'Erro ao atualizar classe processual');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item) {
    if (!window.confirm(`Excluir a classe "${item.name}"?`)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await apiService.delete(`/settings/process-classes/${item.id}`);
      setSuccess('Classe processual excluída');
      await fetchClasses({ silent: true });
    } catch (err) {
      setSuccess(null);
      setError(err.response?.data?.error || 'Erro ao excluir classe processual');
    }
  }

  const included = classes.filter((item) => item.kind === 'INCLUDE');
  const excluded = classes.filter((item) => item.kind === 'EXCLUDE');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-gray-600 max-w-3xl">
          Estas classes valem para o fluxo de ingestão DJE por esfera e tribunal.
          A publicação precisa conter ao menos uma classe de <strong>aceite</strong> da
          mesma esfera (e tribunal, se configurado) e não pode conter nenhuma classe de <strong>exclusão</strong>.
          O mesmo nome pode existir nas duas regras, desde que as esferas não se repitam.
          Exemplo: Aceitar em Comum e Federal, Excluir em Trabalho.
        </p>
        {!readOnly && (
          <button
            onClick={() => setShowForm((current) => !current)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
              showForm
                ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? 'Fechar' : 'Nova Classe'}
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <Check className="w-4 h-4" />
          {success}
        </div>
      )}

      {showForm && !readOnly && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Nova classe processual</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da classe *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value.toUpperCase())}
                  placeholder="Ex: CUMPRIMENTO DE SENTENÇA"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Regra
                </label>
                <select
                  value={kind}
                  onChange={(event) => setKind(event.target.value)}
                  className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {KIND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Esferas de justiça aplicáveis *
              </label>
              <JusticeTypePicker
                idPrefix="create-justice"
                value={justiceTypes}
                onChange={(next) => {
                  const nextTypes = selectedJusticeTypes(next);
                  setJusticeTypes(next);
                  setTribunalSiglas((current) =>
                    current.filter((sigla) => {
                      const tribunal = tribunals.find((item) => item.sigla === sigla);
                      return tribunal && nextTypes.includes(tribunal.justiceType);
                    }),
                  );
                }}
              />
            </div>

            <TribunalPicker
              tribunals={tribunals}
              selectedSiglas={tribunalSiglas}
              justiceTypes={selectedJusticeTypes(justiceTypes)}
              onChange={setTribunalSiglas}
            />

            <p className="text-xs text-gray-500">
              {KIND_OPTIONS.find((option) => option.value === kind)?.hint}
            </p>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              <Plus className="w-4 h-4" />
              {saving ? 'Cadastrando...' : 'Cadastrar Classe'}
            </button>
          </form>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Classes do pipeline</h2>
          <p className="text-sm text-gray-600">
            {classes.length} {classes.length === 1 ? 'classe configurada' : 'classes configuradas'}
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              <p className="mt-2 text-gray-600">Carregando classes...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { key: 'INCLUDE', items: included, empty: 'Nenhuma classe de aceite' },
                { key: 'EXCLUDE', items: excluded, empty: 'Nenhuma classe de exclusão' },
              ].map((column) => {
                const meta = kindMeta(column.key);
                return (
                  <div key={column.key} className={`${meta.column} border rounded-lg overflow-hidden`}>
                    <div className={`${meta.header} px-3 py-2 flex items-center justify-between`}>
                      <span className="font-semibold text-xs">{meta.label}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-white/70 min-w-[18px] text-center font-medium">
                        {column.items.length}
                      </span>
                    </div>
                    <div className="p-2 space-y-1.5 min-h-[200px]">
                      {column.items.length === 0 ? (
                        <div className="text-center py-6 text-gray-400">
                          <FileText className="w-6 h-6 mx-auto mb-1 opacity-30" />
                          <p className="text-xs">{column.empty}</p>
                        </div>
                      ) : (
                        column.items.map((item) => (
                          <div
                            key={item.id}
                            className="bg-white p-2 rounded border border-gray-200 hover:shadow-sm transition-all group"
                          >
                            {editingId === item.id ? (
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  value={editForm.name}
                                  onChange={(event) =>
                                    setEditForm((prev) => ({
                                      ...prev,
                                      name: event.target.value.toUpperCase(),
                                    }))
                                  }
                                  className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:border-blue-500 bg-white text-gray-900"
                                />
                                <select
                                  value={editForm.kind}
                                  onChange={(event) =>
                                    setEditForm((prev) => ({ ...prev, kind: event.target.value }))
                                  }
                                  className="w-full text-xs px-2 py-1 border border-gray-300 rounded bg-white text-gray-900"
                                >
                                  {KIND_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                <JusticeTypePicker
                                  idPrefix={`edit-${item.id}`}
                                  value={editForm.justiceTypes}
                                  onChange={(next) => {
                                    const nextTypes = selectedJusticeTypes(next);
                                    setEditForm((prev) => ({
                                      ...prev,
                                      justiceTypes: next,
                                      tribunalSiglas: (prev.tribunalSiglas || []).filter((sigla) => {
                                        const tribunal = tribunals.find((row) => row.sigla === sigla);
                                        return tribunal && nextTypes.includes(tribunal.justiceType);
                                      }),
                                    }));
                                  }}
                                />
                                <TribunalPicker
                                  compact
                                  tribunals={tribunals}
                                  selectedSiglas={editForm.tribunalSiglas || []}
                                  justiceTypes={selectedJusticeTypes(editForm.justiceTypes)}
                                  onChange={(next) =>
                                    setEditForm((prev) => ({ ...prev, tribunalSiglas: next }))
                                  }
                                />
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handleUpdate(item.id)}
                                    disabled={saving}
                                    className="p-0.5 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                                    title="Salvar"
                                  >
                                    <Check className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => setEditingId(null)}
                                    className="p-0.5 text-gray-400 hover:bg-gray-50 rounded"
                                    title="Cancelar"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <h4 className="font-medium text-gray-900 text-xs leading-tight">
                                    {item.name}
                                  </h4>
                                  <div className="flex flex-wrap items-center gap-1 mt-1">
                                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.badge}`}>
                                      {meta.label}
                                    </span>
                                    {(item.justiceTypes || []).map((tipo) => {
                                      const option = JUSTICE_OPTIONS.find((itemOption) => itemOption.key === tipo);
                                      return (
                                        <span
                                          key={tipo}
                                          className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${option?.color || 'bg-gray-100 text-gray-600'}`}
                                        >
                                          {option?.label || tipo}
                                        </span>
                                      );
                                    })}
                                    {(item.tribunalSiglas || []).length > 0 ? (
                                      (item.tribunalSiglas || []).slice(0, 4).map((sigla) => (
                                        <span
                                          key={sigla}
                                          className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700"
                                        >
                                          {sigla}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">
                                        Todos os tribunais
                                      </span>
                                    )}
                                    {(item.tribunalSiglas || []).length > 4 && (
                                      <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">
                                        +{(item.tribunalSiglas || []).length - 4}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {!readOnly && (
                                  <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-all">
                                    <button
                                      onClick={() => {
                                        setEditingId(item.id);
                                        setEditForm({
                                          name: item.name,
                                          kind: item.kind,
                                          justiceTypes: toJusticeSelection(item.justiceTypes),
                                          tribunalSiglas: item.tribunalSiglas || [],
                                        });
                                      }}
                                      className="p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                      title="Editar"
                                    >
                                      <Edit className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(item)}
                                      className="p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                      title="Excluir"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
